import axios from 'axios';

// In production the dashboard is served under a path prefix (PUBLIC_URL, set from
// the "homepage" field). The API lives behind that same prefix, so a bare "/api"
// would escape it and hit whatever else is mounted at the domain root.
export const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/api'
  : (process.env.REACT_APP_API_URL || `${process.env.PUBLIC_URL || ''}/api`);

// Origin that serves uploaded assets (the API base without its trailing "/api").
// Dev → "http://localhost:5000"; prod → "" (same origin as the dashboard).
export const ASSET_BASE = API_BASE.replace(/\/api\/?$/, '');

/**
 * Resolve a stored asset URL for display in the dashboard. Uploaded banners are
 * stored with an absolute host (e.g. "http://localhost:5000/uploads/...") that
 * only resolves from the server machine. When the dashboard is viewed through a
 * tunnel or a different host, that baked-in host is unreachable. Re-point any
 * "/uploads/..." path at the dashboard's own origin so the image always loads.
 */
export const resolveAssetUrl = (url) => {
  if (!url) return '';
  // Our own locally-served uploads: re-point the "/uploads/..." path at the
  // dashboard's origin (handles a baked-in "localhost:5000" or a relative path).
  const i = url.indexOf('/uploads/');
  if (i >= 0) return `${ASSET_BASE}${url.slice(i)}`;
  // Anything else (e.g. a public E2E bucket URL) is already reachable — use as-is.
  return url;
};

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      const isChangePasswordRequest = error.config?.url?.includes('/auth/change-password');
      if (!isLoginRequest && !isChangePasswordRequest) {
        localStorage.removeItem('token');
        // Full page navigation, so it needs the deployment prefix — a bare "/login"
        // escapes the app and lands on whatever is mounted at the domain root.
        window.location.href = `${process.env.PUBLIC_URL || ''}/login`;
      }
    }
    
    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response?.data);
    }
    
    return Promise.reject(error);
  }
);

// API helper functions
export const apiHelpers = {
  // GET request
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await api.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // File upload
  upload: async (url, formData, config = {}) => {
    try {
      const response = await api.post(url, formData, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// Error handling utility
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return {
          type: 'validation',
          message: data.message || 'Invalid request data',
          errors: data.errors || {},
        };
      
      case 401:
        return {
          type: 'auth',
          message: data.message || 'Authentication required',
        };
      
      case 403:
        return {
          type: 'permission',
          message: data.message || 'Access denied',
        };
      
      case 404:
        return {
          type: 'not_found',
          message: data.message || 'Resource not found',
        };
      
      case 422:
        return {
          type: 'validation',
          message: data.message || 'Validation failed',
          errors: data.errors || {},
        };
      
      case 429:
        return {
          type: 'rate_limit',
          message: data.message || 'Too many requests',
        };
      
      case 500:
        return {
          type: 'server',
          message: data.message || 'Internal server error',
        };
      
      default:
        return {
          type: 'unknown',
          message: data.message || 'An error occurred',
        };
    }
  } else if (error.request) {
    // Network error
    return {
      type: 'network',
      message: 'Network error. Please check your connection.',
    };
  } else {
    // Other error
    return {
      type: 'unknown',
      message: error.message || 'An error occurred',
    };
  }
};

// Query parameter builder
export const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item));
      } else {
        searchParams.append(key, value);
      }
    }
  });
  
  return searchParams.toString();
};

// Pagination helper
export const createPaginationParams = (page = 1, limit = 10, filters = {}) => {
  return {
    page,
    limit,
    ...filters,
  };
};

export default api; 